import { z } from 'zod';
import { DirectPaymentStatuses } from '@/constants/DirectPaymentStatus';

export const selectAccountResponseSchema = z.object({
  orderId: z.string(),
  response: z.string(),
});

export const initiateDirectPaymentResponseSchema = z.object({
  paymentProviderToken: z.string(),
});

export const directPaymentStatusResponseSchema = z.object({
  status: z.enum(DirectPaymentStatuses),
});

export const directPaymentSubItemsSchema = z
  .object({
    credit: z.number().optional(),
    debtConsolidation: z.number().optional(),
    paymentProtectionInsurance: z.number().optional(),
  })
  .refine(data => Object.values(data).some(value => value !== undefined), {
    error: 'At least one of the fields must be defined',
  });

export const directPaymentItemSchema = z.object({
  dueDate: z.iso.datetime(),
  discount: z.number(),
  totalAmountAfterDiscount: z.number(),
  totalAmount: z.number(),
  items: directPaymentSubItemsSchema,
});

export const paidSchema = z.object({
  isPaid: z.literal(true),
});

export const noDiscountSchema = z.object({
  discountCategory: z.literal('NO_DISCOUNT'),
  isPaid: z.literal(false).optional(),
  details: z.object({
    // If another status like "under-termination" or "defaulted" is requested,
    // we need to update the schema with a "status" field that's a string union
    isUnderTermination: z.boolean(),
    isDefaulted: z.boolean(),
    payments: z.array(directPaymentItemSchema),
    amountToPay: z.number(),
  }),
});

export const overdueCampaignSchema = z.object({
  discountCategory: z.literal('OVERDUE_CAMPAIGN'),
  isPaid: z.literal(false).optional(),
  details: z.object({
    payments: z.array(directPaymentItemSchema),
    amountToPay: z.number(),
    amountToPayAfterDiscount: z.number(),
    discount: z.number(),
  }),
});

const comparisonItemSchema = z.object({
  amountToPay: z.number(),
  installmentAmount: z.number(),
  outstandingAmountAfterPayment: z.number(),
});

export const resetPaybackPlanSchema = z.object({
  discountCategory: z.literal('RESET_PAYBACK_PLAN'),
  isPaid: z.literal(false).optional(),
  details: z.object({
    isUnderTermination: z.boolean(),
    comparison: z.object({
      offer: comparisonItemSchema,
      current: comparisonItemSchema,
    }),
    offerCost: z.number(),
    nextRepaymentDate: z.iso.datetime(),
  }),
});

export const directPaymentResponseSchema = z.union([
  paidSchema,
  z.discriminatedUnion('discountCategory', [noDiscountSchema, overdueCampaignSchema, resetPaybackPlanSchema]),
]);
